import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Alert, Button, Form, Placeholder, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faMagnifyingGlass, faPlus } from "@fortawesome/free-solid-svg-icons";
import { crmPostventaV2Api } from "../../crmpostventa-v2/crmPostventaV2Api";
import OnboardingAlertCard from "./OnboardingAlertCard";
import { filterOnboardingRows, normalizeOnboardingAlertItem, normalizeText } from "./onboardingAlertsUtils";
import "./onboarding-alerts.css";

const FETCH_PAGE_SIZE = 200;
const MOBILE_BREAKPOINT = 900;
const SEARCH_DEBOUNCE_MS = 300;
const emptyFilters = { search: "", date: "" };

const buildGenerationSummary = (response) =>
  [
    response?.message || "Sin mensaje.",
    Number.isFinite(response?.elegibles) ? `Elegibles: ${response.elegibles}` : null,
    Number.isFinite(response?.crear) ? `A crear en esta corrida: ${response.crear}` : null,
    Number.isFinite(response?.pendientesPorLimite) ? `Pendientes por límite: ${response.pendientesPorLimite}` : null,
    response?.motivoBloqueoEjecucionReal || null,
  ]
    .filter(Boolean)
    .join("\n");

export default function OnboardingAlertsSection({
  processCode,
  processSlug,
  title,
  emptyMessage,
  globalFilters,
  onManage,
  onAssign,
  onRelease,
  onTake,
  onMutationNonce = 0,
}) {
  const [filters, setFilters] = useState(emptyFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [state, setState] = useState({ loading: true, rows: [], total: 0, error: "", permissions: null });
  const busyAction = "";
  const setBusyAction = () => {};
  const [viewportMobile, setViewportMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  useEffect(() => {
    const onResize = () => setViewportMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadRows = async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      let pageNumber = 1;
      let total = 0;
      let mergedRows = [];

      while (pageNumber === 1 || mergedRows.length < total) {
        const response = await crmPostventaV2Api.obtenerAlertas({
          procesoCodigo: processCode,
          fechaReferencia: globalFilters?.fechaReferencia,
          prioridad: globalFilters?.prioridad || undefined,
          estado: globalFilters?.estado || undefined,
          texto: globalFilters?.texto || undefined,
          pagina: pageNumber,
          tamanoPagina: FETCH_PAGE_SIZE,
        });

        const currentRows = Array.isArray(response?.filas) ? response.filas : [];
        total = Number(response?.total || currentRows.length || 0);
        mergedRows = mergedRows.concat(currentRows);

        if (!currentRows.length || mergedRows.length >= total) {
          setState({
            loading: false,
            rows: mergedRows.map((item) => normalizeOnboardingAlertItem({ ...item, procesoSlug: processSlug })),
            total,
            permissions: response?.permisos || null,
            error: "",
          });
          return;
        }

        pageNumber += 1;
      }
    } catch {
      setState({
        loading: false,
        rows: [],
        total: 0,
        permissions: null,
        error: "No pudimos cargar los procesos de onboarding.",
      });
    }
  };

  useEffect(() => {
    loadRows();
  }, [globalFilters?.estado, globalFilters?.fechaReferencia, globalFilters?.prioridad, globalFilters?.texto, processCode, processSlug]);

  useEffect(() => {
    if (onMutationNonce > 0) loadRows();
  }, [onMutationNonce]);

  const filteredRows = useMemo(
    () => filterOnboardingRows(state.rows, { ...filters, search: debouncedSearch }),
    [debouncedSearch, filters, state.rows]
  );

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((value) => Boolean(normalizeText(value))),
    [filters]
  );

  const canGenerate = false;

  const handleGenerate = async () => {
    setBusyAction("generate");
    try {
      const preview = await crmPostventaV2Api.generar(processSlug, {
        dryRun: true,
        fecha: globalFilters?.fechaReferencia,
      });

      const confirmation = await Swal.fire({
        title: "Generar onboarding",
        html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${buildGenerationSummary(preview)}</pre>`,
        icon: preview?.permiteEjecucionReal === false ? "warning" : "question",
        showCancelButton: true,
        confirmButtonText: "+ Generar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmation.isConfirmed || preview?.permiteEjecucionReal === false) return;

      const response = await crmPostventaV2Api.generar(processSlug, {
        dryRun: false,
        fecha: globalFilters?.fechaReferencia,
      });

      await Swal.fire(title, response?.message || "Generación completada.", "success");
      await loadRows();
    } catch (error) {
      Swal.fire(title, error?.message || "No se pudo generar onboarding.", "error");
    } finally {
      setBusyAction("");
    }
  };

  return (
    <section className="onboarding-alerts-section">
      <div className="onboarding-alerts__header">
        <div className="onboarding-alerts__title-block">
          <h3>{title}</h3>
          <p>Alta, checklist obligatorio y derivación inicial.</p>
        </div>

        <div className="onboarding-alerts__header-actions">
          {canGenerate ? (
            <Button type="button" className="onboarding-alerts__generate-btn" onClick={handleGenerate} disabled={busyAction === "generate"}>
              {busyAction === "generate" ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faPlus} />}
              <span>Generar</span>
            </Button>
          ) : null}
          <button type="button" className="onboarding-alerts__refresh-btn" onClick={loadRows} disabled={state.loading || busyAction === "generate"} aria-label="Actualizar onboarding">
            {state.loading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faArrowsRotate} />}
          </button>
        </div>
      </div>

      <div className="onboarding-alerts__filters">
        <div className="onboarding-alerts__search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <Form.Control
            type="search"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Buscar por comercio, CUIT, terminal, responsable, teléfono..."
            aria-label="Buscar procesos de onboarding"
          />
        </div>
        <Form.Control type="date" value={filters.date} onChange={(event) => setFilters((prev) => ({ ...prev, date: event.target.value }))} aria-label="Filtrar onboarding por fecha de próxima acción" />
      </div>

      {!viewportMobile ? (
        <div className="onboarding-alerts__table-head">
          <span>Comercio</span>
          <span>Seguimiento</span>
          <span>Próx. acción</span>
          <span>Prioridad</span>
          <span>Responsable</span>
          <span>Acciones</span>
        </div>
      ) : null}

      {state.loading ? (
        <div className="onboarding-alerts__list is-loading">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="onboarding-alerts__skeleton-card">
              <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
            </div>
          ))}
        </div>
      ) : state.error ? (
        <Alert variant="warning" className="onboarding-alerts__error">
          <div>{state.error}</div>
          <Button type="button" variant="outline-secondary" size="sm" onClick={loadRows}>Reintentar</Button>
        </Alert>
      ) : !state.rows.length ? (
        <Alert variant="secondary" className="mb-0">{emptyMessage || "No hay procesos de onboarding pendientes."}</Alert>
      ) : !filteredRows.length ? (
        <Alert variant="secondary" className="mb-0">No se encontraron procesos de onboarding con los filtros seleccionados.</Alert>
      ) : (
        <div className="onboarding-alerts__list">
          {filteredRows.map((item) => (
            <OnboardingAlertCard key={item.id} item={item} mobile={viewportMobile} busy={busyAction === "generate"} onManage={onManage} onTake={onTake} onAssign={onAssign} onRelease={onRelease} />
          ))}
        </div>
      )}

      {state.permissions && !state.permissions?.puedeGestionarPostventaGeneral && !state.permissions?.puedeAdministrarPostventa ? (
        <div className="onboarding-alerts__permission-note">No tenés permisos para gestionar procesos de onboarding.</div>
      ) : null}

      {hasActiveFilters ? (
        <div className="onboarding-alerts__footer">
          <Button type="button" variant="light" onClick={() => setFilters(emptyFilters)}>Limpiar filtros</Button>
        </div>
      ) : null}
    </section>
  );
}
